import { soundManager } from '@slot-nx/core'
import { Container, Assets } from 'pixi.js'
import { SymbolType } from '../game/enum/SymbolType'

import { Spine } from '@esotericsoftware/spine-pixi'

import dataAKQJ from '/resource/spine/symbol/AKQJ/AKQJ.skel?url'

import atlasAKQJ from '/resource/spine/symbol/AKQJ/AKQJ.atlas?url'

import dataMsymbol from '/resource/spine/symbol/Msymbol/Msymbol.skel?url'

import atlasMsymbol from '/resource/spine/symbol/Msymbol/Msymbol.atlas?url'

import dataS1 from '/resource/spine/symbol/S1/spine_symbol_s1_v2.skel?url'

import atlasS1 from '/resource/spine/symbol/S1/spine_symbol_s1_v2.atlas?url'

import dataBay from '/resource/spine/symbol/Bay/Multiple.skel?url'

import atlasBay from '/resource/spine/symbol/Bay/Multiple.atlas?url'
import dataScatter from '/resource/spine/symbol/Scatter/spine_symbol_sc_v3.skel?url'
import atlasScatter from '/resource/spine/symbol/Scatter/spine_symbol_sc_v3.atlas?url'

import { sleep } from '../game/util/gameUtil'
import { gameConfig } from '../../config'
import { BombSymbolType, ScatterSymbolType } from './enum/animationType'
import { WinningSymbolType } from '../result/service/ResultService'
import gsap from 'gsap'
import { useGameStore } from '../game/store/GameStore'
import { SweetBombSoundType } from '../soundManager/enum/soundType'

export class SymbolSpineUtil {
  public spineMap = new Map<string, Spine>()
  public upperReel: Container
  private scatterAnimationSwitchDelayMs = 50

  public async bombSymbolAnimation({
    symbolInfo,
        animationName,
        isLoop = false,
        textEventName,
        textAnimation,
  }: {
    symbolInfo: WinningSymbolType
    animationName: BombSymbolType
    isLoop?: boolean
    textEventName: BombSymbolType
    textAnimation: () => void
  }) {
    const wrapper = symbolInfo.node.getChildAt(0) as Container

    // check laster spin
    const lastSpin = wrapper.getChildByName<Spine>('spine')
    if (lastSpin) {
      lastSpin.destroy()
    }

    // get spin
    await Assets.load([dataS1, atlasS1])
    const spine = Spine.from(dataS1, atlasS1)
    spine.name = 'spine'
    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2
    wrapper.getChildAt(0).alpha = 0
    wrapper.addChild(spine)
    const track = spine.state.setAnimation(0, animationName, isLoop)
    spine.state.addListener({
      async event(entry, event) {
        if (event.data.name === `${textEventName}`) {
          textAnimation()
        }
      },
    })
    if (isLoop) return
    await sleep((track.animation?.duration as number) * 1000)
    this.destroySpine({ node: symbolInfo.node, spine })
  }

  private destroySpine({ node, spine }: { node: Container, spine: Spine }) {
    node.removeChild(spine)
    spine.destroy()
  }

  public async loadAllMultiplierSpine() {
    await Assets.load([dataBay, atlasBay])
    const spine = Spine.from(dataBay, atlasBay)
    return spine
  }

  private async symbolAnimation() {
    await Assets.load([dataAKQJ, atlasAKQJ])
    return Spine.from(dataAKQJ, atlasAKQJ)
  }

  private async mSymbolAnimation() {
    await Assets.load([dataMsymbol, atlasMsymbol])
    return Spine.from(dataMsymbol, atlasMsymbol)
  }

  private async scatterSymbolAnimation() {
    await Assets.load([dataScatter, atlasScatter])
    return Spine.from(dataScatter, atlasScatter)
  }

  public async symbolWin({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    const spine = await this.getSpine({ symbolInfo })
    const wrapper = symbolInfo.node.getChildAt(0) as Container

    if (!spine) return
    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2
    wrapper.getChildAt(0).alpha = 0
    wrapper.addChild(spine)
    const animationName = SymbolType[Number(symbolInfo.node.name)]
    const winAnimation = spine.state.setAnimation(0, `symbol_${animationName}_win`, false)
    await sleep((winAnimation.animation?.duration as number) * 1000)
  }

  public async symbolLoop({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    const wrapper = (symbolInfo.node as Container).getChildAt(0) as Container
    const spine = wrapper.children.find(child => child instanceof Spine) as Spine
    if (!spine) return
    const animationName = SymbolType[Number(symbolInfo.node.name)]
    spine.state.setAnimation(0, `symbol_${animationName}_loop`, false)
    await sleep(1000)
  }

  public async symbolEliminate({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
    const wrapper = (symbolInfo.node as Container).getChildAt(0) as Container
    const spine = isThisSpinInSpeedMode ? await this.getSpine({ symbolInfo }) : wrapper.children.find(child => child instanceof Spine) as Spine

    if (!spine) return
    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2
    wrapper.getChildAt(0).alpha = 0
    wrapper.addChild(spine)
    const animationName = SymbolType[Number(symbolInfo.node.name)]
    const outAnimation = spine.state.setAnimation(0, `symbol_${animationName}_out`, false)
    await sleep((outAnimation.animation?.duration as number) * 1000)
  }

  private async getSpine({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    const symbol = Number(symbolInfo.node.name)
    switch (symbol) {
      case SymbolType.A:
      case SymbolType.J:
      case SymbolType.Q:
      case SymbolType.K:
        return await this.symbolAnimation()
      case SymbolType.M1:
      case SymbolType.M2:
      case SymbolType.M3:
      case SymbolType.M4:
      case SymbolType.M5:
        return await this.mSymbolAnimation()
    }
  }

  public async dropSymbol({ symbolInfo, dropSpace, symbolIndex, isNewSymbol }: { symbolInfo: WinningSymbolType, dropSpace: number, symbolIndex: number, isNewSymbol: boolean }) {
    const { node } = symbolInfo
    const wrapper = node.getChildAt(0) as Container
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode

    wrapper.getChildAt(0).alpha = 1

    await gsap.to(node, {
      duration: isThisSpinInSpeedMode ? gameConfig.speedModeSpinDuration : gameConfig.spinDuration,
      ease: 'power3.inOut',
      delay: isNewSymbol ? (symbolIndex * gameConfig.symbolDelay) : 0,
      y: node.y + dropSpace * gameConfig.nodeHeight,
    })

    if (dropSpace * gameConfig.nodeHeight > 0) {
      await this.playLandingBounce({ node: node as Container })
    }

    const symbolType = Number(symbolInfo.node.name)
    if (isNewSymbol && (symbolType === SymbolType.Bonus || symbolType === SymbolType.SuperBonus)) {
      await this.scatterIn({ node: symbolInfo.node as Container, isSuper: symbolType === SymbolType.SuperBonus })
    }
  }

  private playLandingBounce({ node }: { node: Container }) {
    const spriteContainer = node.getChildAt(0) as Container
    const symbolSprite = spriteContainer.getChildAt(0)
    const baseSec = 0.015
    const bounceTask = gsap.timeline()
    bounceTask.to(symbolSprite.scale, {
      x: '+=0.1',
      y: '-=0.1',
      duration: 5 * baseSec,
    })
      .to(symbolSprite.position, {
        y: '+=10',
        duration: 5 * baseSec,
      }, '<')
      .to(symbolSprite.scale, {
        x: '-=0.125',
        y: '+=0.125',
        duration: 7 * baseSec,
      })
      .to(symbolSprite.position, {
        y: '-=18',
        duration: 7 * baseSec,
      }, '<')
      .to(symbolSprite.scale, {
        x: '+=0.025',
        y: '-=0.025',
        duration: 3.2 * baseSec,
      })
      .to(symbolSprite.position, {
        y: '+=8',
        duration: 3.2 * baseSec,
      }, '<')
    return bounceTask
  }

  public async scatterIn({ node, isSuper }: { node: Container, isSuper: boolean }) {
    const spine = await this.scatterSymbolAnimation()

    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2

    node.addChild(spine)

    const animationName = isSuper ? ScatterSymbolType.SuperScatterSymbolIn : ScatterSymbolType.ScatterSymbolTypeIn
    const animation = spine.state.setAnimation(0, animationName, false)
    await sleep(this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 0
    if (isSuper) {
      soundManager.play(SweetBombSoundType.SymScatter2)
    }
    else {
      soundManager.play(SweetBombSoundType.SymScatter1)
    }
    await sleep((animation.animation?.duration as number) * 1000 - this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 1
    this.destroySpine({ node, spine })
  }

  public async scatterWin({ node, isSuper }: { node: Container, isSuper: boolean }) {
    const spine = await this.scatterSymbolAnimation()

    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2
    node.addChild(spine)

    const animationName = isSuper ? ScatterSymbolType.SuperScatterSymbolWin : ScatterSymbolType.ScatterSymbolTypeWin
    const animation = spine.state.setAnimation(0, animationName, false)
    await sleep(this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 0
    await sleep((animation.animation?.duration as number) * 1000 - this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 1
    this.destroySpine({ node, spine })
    const resolve = Promise.resolve()
    return resolve
  }

  public async scatterPeek({ node, isSuper }: { node: Container, isSuper: boolean }) {
    const spine = await this.scatterSymbolAnimation()
    spine.x = gameConfig.nodeWidth / 2
    spine.y = gameConfig.nodeHeight / 2
    node.addChild(spine)

    const animationName = isSuper ? ScatterSymbolType.SuperScatterSymbolPeek : ScatterSymbolType.ScatterSymbolTypePeek
    const animation = spine.state.setAnimation(0, animationName, false)
    await sleep(this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 0
    soundManager.play(SweetBombSoundType.ScatterPeek)
    await sleep((animation.animation?.duration as number) * 1000 - this.scatterAnimationSwitchDelayMs)
    node.getChildAt(0).alpha = 1
    this.destroySpine({ node, spine })
    const resolve = Promise.resolve()
    return resolve
  }

  public async removeSpine({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    const container = (symbolInfo.node as Container).getChildAt(0) as Container
    container.children.forEach((child) => {
      if (child instanceof Spine) {
        child.destroy()
      }
    })
    symbolInfo.node.y = -gameConfig.nodeHeight * symbolInfo.moveParameters
  }
}
